import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useTeacher } from '../../context/TeacherContext';
import apiAxiosInstance from '../../utils/axiosInstance';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import html2pdf from 'html2pdf.js';
import htmlDocx from 'html-docx-js/dist/html-docx';
import { saveAs } from 'file-saver';

interface Student {
  _id: string;
  firstName: string;
  lastName: string;
  groupIds?: string[];
}

interface Group {
  _id: string;
  name: string;
  students: Student[];
}

interface ObservationResponseItem {
  _id: string;
  student: { _id: string; firstName: string; lastName: string } | string;
  group: string;
  lessonPlan: {
    _id: string;
    standard?: { code?: string; description?: string } | string;
  } | string | null;
  observation: string;
  date: string;
  storyTitle?: string;
}

const DeliveredLessons: React.FC = () => {
  const { teacher } = useTeacher();
  const [groups, setGroups] = useState<Group[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [rows, setRows] = useState<any[]>([]);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        setError('');
        // Fetch groups and students in parallel
        const [groupsResp, studentsResp] = await Promise.all([
          apiAxiosInstance.get('/api/groups', { params: { teacherId: teacher?._id } }),
          apiAxiosInstance.get('/api/students', { params: { teacherId: teacher?._id } }),
        ]);
        setGroups(groupsResp.data || []);
        setStudents(studentsResp.data || []);
      } catch (e: any) {
        setError(typeof e === 'string' ? e : (e?.message || 'Failed to load data'));
      }
    };
    if (teacher?._id) {
      loadInitial();
    }
  }, [teacher?._id]);

  const filteredStudents = useMemo(() => {
    if (!selectedGroupId) return students;
    const group = groups.find(g => g._id === selectedGroupId);
    const groupStudentIds = new Set((group?.students || []).map(s => s._id));
    return students.filter(s => groupStudentIds.has(s._id));
  }, [selectedGroupId, groups, students]);

  const handleFetchObservations = async (studentId: string, groupId?: string) => {
    if (!studentId) return;
    try {
      setLoading(true);
      setError('');
      console.log('Fetching observations for studentId:', studentId, 'groupId:', groupId);
      const resp = await apiAxiosInstance.get('/api/student-observations', {
        params: {
          studentId,
          ...(groupId ? { groupId } : {}),
        }
      });
      console.log('Observations response:', resp.data);
      const observations: ObservationResponseItem[] = resp.data?.observations || [];
      const mapped = observations.map((obs, idx) => {
        const lp: any = obs.lessonPlan && typeof obs.lessonPlan === 'object' ? obs.lessonPlan : null;
        const standard: any = lp?.standard && typeof lp.standard === 'object' ? lp.standard : null;
        return {
          id: obs._id || `${idx}`,
          date: dayjs(obs.date).format('YYYY-MM-DD'),
          title: obs.storyTitle || '',
          standard: standard?.code || '',
          description: standard?.description || '',
          observation: obs.observation || '',
        };
      });
      console.log('Mapped observations:', mapped);
      setRows(mapped);
    } catch (e: any) {
      console.error('Error fetching observations:', e);
      setError(typeof e === 'string' ? e : (e?.message || 'Failed to fetch observations'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStudentId) {
      handleFetchObservations(selectedStudentId, selectedGroupId || undefined);
    } else {
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudentId, selectedGroupId]);

  const columns: GridColDef[] = [
    { field: 'date', headerName: 'Date Delivered', flex: 0.6, minWidth: 140 },
    { field: 'title', headerName: 'Title', flex: 1, minWidth: 180 },
    { field: 'standard', headerName: 'Standard', flex: 0.7, minWidth: 140 },
    { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 240 },
    { field: 'observation', headerName: 'Observation', flex: 1.5, minWidth: 240 },
  ];

  const selectedStudent = students.find(s => s._id === selectedStudentId);
  const selectedGroup = groups.find(g => g._id === selectedGroupId);

  const generateExportContent = () => {
    const studentName = selectedStudent ? `${selectedStudent.lastName}, ${selectedStudent.firstName}` : 'All Students';
    const groupName = selectedGroup ? selectedGroup.name : 'All Groups';
    const reportDate = dayjs().format('MMMM DD, YYYY');
    
    return `
      <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; color: #333;">
        <style>
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            .page-break { page-break-before: always; }
            .no-break { page-break-inside: avoid; }
          }
          
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #1976d2;
          }
          
          .title {
            font-size: 28px;
            font-weight: 700;
            color: #1976d2;
            margin: 0 0 10px 0;
          }
          
          .subtitle {
            font-size: 16px;
            color: #666;
            margin: 0;
          }
          
          .info-section {
            background-color: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 25px;
            border-left: 4px solid #1976d2;
          }
          
          .info-row {
            display: flex;
            margin-bottom: 8px;
            align-items: center;
          }
          
          .info-label {
            font-weight: 600;
            color: #555;
            min-width: 120px;
            margin-right: 10px;
          }
          
          .info-value {
            color: #333;
            flex: 1;
          }
          
          .lesson-item {
            background-color: #ffffff;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            margin-bottom: 20px;
            padding: 20px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            page-break-inside: avoid;
          }
          
          .lesson-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px solid #f0f0f0;
          }
          
          .lesson-title {
            font-size: 18px;
            font-weight: 600;
            color: #1976d2;
            margin: 0;
            flex: 1;
          }
          
          .lesson-date {
            font-size: 14px;
            color: #666;
            background-color: #e3f2fd;
            padding: 4px 12px;
            border-radius: 16px;
            white-space: nowrap;
            margin-left: 15px;
          }
          
          .lesson-standard {
            font-size: 14px;
            color: #555;
            margin-bottom: 10px;
            font-weight: 500;
          }
          
          .lesson-description {
            font-size: 14px;
            color: #666;
            margin-bottom: 15px;
            line-height: 1.5;
            font-style: italic;
          }
          
          .observation-section {
            background-color: #f8f9fa;
            padding: 15px;
            border-radius: 6px;
            border-left: 4px solid #4caf50;
          }
          
          .observation-label {
            font-weight: 600;
            color: #2e7d32;
            margin-bottom: 8px;
            font-size: 14px;
          }
          
          .observation-text {
            color: #333;
            line-height: 1.6;
            margin: 0;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e0e0e0;
            text-align: center;
            color: #666;
            font-size: 12px;
          }
        </style>
        
        <div class="header">
          <h1 class="title">Delivered Lessons Report</h1>
          <p class="subtitle">Student Progress and Observations</p>
        </div>
        
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">Student:</span>
            <span class="info-value">${studentName}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Group:</span>
            <span class="info-value">${groupName}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Report Date:</span>
            <span class="info-value">${reportDate}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Total Lessons:</span>
            <span class="info-value">${rows.length}</span>
          </div>
        </div>
        
        ${rows.length === 0 ? `
          <div style="text-align: center; padding: 40px; color: #666; font-style: italic;">
            No delivered lessons found for the selected criteria.
          </div>
        ` : rows.map((row, index) => `
          <div class="lesson-item no-break">
            <div class="lesson-header">
              <h3 class="lesson-title">${row.title || 'Untitled Lesson'}</h3>
              <span class="lesson-date">${row.date}</span>
            </div>
            
            ${row.standard ? `
              <div class="lesson-standard">
                <strong>Standard:</strong> ${row.standard}
              </div>
            ` : ''}
            
            ${row.description ? `
              <div class="lesson-description">
                ${row.description}
              </div>
            ` : ''}
            
            <div class="observation-section">
              <div class="observation-label">Teacher Observation:</div>
              <p class="observation-text">${row.observation || 'No observation recorded.'}</p>
            </div>
          </div>
        `).join('')}
        
        <div class="footer">
          <p>Generated on ${reportDate} by Level Up Teaching</p>
        </div>
      </div>
    `;
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Delivered Lessons Report</title>
            <style>
              body { margin: 0; padding: 0; }
              @media print {
                @page { margin: 0.5in; }
              }
            </style>
          </head>
          <body>
            ${generateExportContent()}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  const handlePDFExport = async () => {
    if (!printRef.current) return;
    
    const content = generateExportContent();
    const element = document.createElement('div');
    element.innerHTML = content;
    
    const opt = {
      margin: 0.5,
      filename: `delivered-lessons-${selectedStudent?.lastName || 'report'}-${dayjs().format('YYYY-MM-DD')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    try {
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('PDF export error:', error);
      setError('Failed to generate PDF. Please try again.');
    }
  };

  const handleDOCExport = () => {
    const content = generateExportContent();
    const element = document.createElement('div');
    element.innerHTML = content;
    
    try {
      const docx = htmlDocx.asBlob(element.innerHTML);
      
      const filename = `delivered-lessons-${selectedStudent?.lastName || 'report'}-${dayjs().format('YYYY-MM-DD')}.docx`;
      saveAs(docx, filename);
    } catch (error) {
      console.error('DOC export error:', error);
      setError('Failed to generate DOC. Please try again.');
    }
  };

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Delivered Lessons by Student
        </Typography>
        
        <ButtonGroup variant="outlined" size="small">
          <Tooltip title="Print Report">
            <Button
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={rows.length === 0}
              sx={{ 
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              Print
            </Button>
          </Tooltip>
          
          <Tooltip title="Download as PDF">
            <Button
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePDFExport}
              disabled={rows.length === 0}
              sx={{ 
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  borderColor: '#c62828',
                  backgroundColor: 'rgba(211, 47, 47, 0.04)'
                }
              }}
            >
              PDF
            </Button>
          </Tooltip>
          
          <Tooltip title="Download as Word Document">
            <Button
              startIcon={<DescriptionIcon />}
              onClick={handleDOCExport}
              disabled={rows.length === 0}
              sx={{ 
                borderColor: '#2e7d32',
                color: '#2e7d32',
                '&:hover': {
                  borderColor: '#1b5e20',
                  backgroundColor: 'rgba(46, 125, 50, 0.04)'
                }
              }}
            >
              DOC
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl sx={{ minWidth: 240 }} size="small">
            <InputLabel id="group-select-label">Group (optional)</InputLabel>
            <Select
              labelId="group-select-label"
              value={selectedGroupId}
              label="Group (optional)"
              onChange={(e) => {
                const newGroupId = e.target.value as string;
                setSelectedGroupId(newGroupId);
                // If current selected student isn't in the new group's roster, clear it
                if (newGroupId) {
                  const group = groups.find(g => g._id === newGroupId);
                  const ids = new Set((group?.students || []).map(s => s._id));
                  if (selectedStudentId && !ids.has(selectedStudentId)) {
                    setSelectedStudentId('');
                  }
                }
              }}
            >
              <MenuItem value="">
                <em>All Groups</em>
              </MenuItem>
              {groups.map(g => (
                <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 280 }} size="small">
            <InputLabel id="student-select-label">Student</InputLabel>
            <Select
              labelId="student-select-label"
              value={selectedStudentId}
              label="Student"
              onChange={(e) => setSelectedStudentId(e.target.value)}
            >
              {filteredStudents.map(s => (
                <MenuItem key={s._id} value={s._id}>{s.lastName}, {s.firstName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Paper sx={{ height: 520, p: 1 }}>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            disableSelectionOnClick
            density="compact"
            sx={{ border: 0 }}
          />
        )}
      </Paper>

      {/* Hidden div for export content generation */}
      <div ref={printRef} style={{ display: 'none' }}>
        {generateExportContent()}
      </div>
    </Box>
  );
};

export default DeliveredLessons;


